import { useEffect, useRef, useState } from "react";
import { Icon } from "../../components/icons";
import { cx } from "../../components/ui";
import { formatBytes } from "../../lib/format";
import {
  ATTACHMENT_ACCEPT,
  MAX_ATTACHMENTS_PER_MESSAGE,
  attachmentProblem,
  isPreviewable,
  withEffectiveType,
} from "../../lib/support";
import { fetchTicketAttachment } from "../../lib/queries";
import type { SupportAttachment } from "../../lib/types";

/**
 * Reading and adding files on a ticket.
 *
 * Both halves live here because they are the same subject and neither is big:
 * one fetches bytes the operator is entitled to, the other collects bytes they
 * are about to send.
 */

/**
 * One attached file, fetched on demand with the operator's token.
 *
 * A button rather than a link — the API authorizes with a bearer token held in
 * memory, and an anchor sends no header. Images expand in place; everything
 * else downloads through a blob this page already holds. The object URL is
 * revoked on unmount, or a long thread keeps every file opened in it alive for
 * the life of the tab.
 */
export function TicketAttachment({
  attachment,
  ticketId,
}: {
  attachment: SupportAttachment;
  ticketId: string;
}) {
  const [preview, setPreview] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const objectUrl = useRef<string | null>(null);

  useEffect(
    () => () => {
      if (objectUrl.current) URL.revokeObjectURL(objectUrl.current);
    },
    [],
  );

  const open = async () => {
    if (busy) return;
    setBusy(true);
    setFailed(false);
    try {
      const blob = await fetchTicketAttachment(ticketId, attachment.id);
      const href = URL.createObjectURL(blob);
      objectUrl.current = href;

      if (isPreviewable(attachment.contentType)) {
        setPreview(href);
      } else {
        const anchor = document.createElement("a");
        anchor.href = href;
        anchor.download = attachment.filename;
        anchor.click();
      }
    } catch {
      setFailed(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-2">
      <button
        type="button"
        onClick={open}
        disabled={busy}
        className="group flex w-full items-center gap-2 border border-line bg-surface px-2.5 py-1.5 text-left text-xs text-fg-muted transition-colors duration-200 ease-smooth hover:border-line-strong hover:text-fg disabled:opacity-60"
      >
        <Icon
          name="paperclip"
          className="size-3.5 shrink-0 text-fg-subtle group-hover:text-accent-hover"
        />
        <span className="min-w-0 flex-1 truncate">{attachment.filename}</span>
        {attachment.uploadedRole === "staff" && (
          <span className="shrink-0 text-fg-subtle">ours</span>
        )}
        <span className="shrink-0 tabular-nums text-fg-subtle">
          {formatBytes(attachment.sizeBytes)}
        </span>
      </button>

      {failed && <p className="text-xs text-danger">That file could not be fetched.</p>}
      {preview && (
        <img
          src={preview}
          alt={attachment.filename}
          className="max-h-96 w-full border border-line object-contain"
        />
      )}
    </div>
  );
}

/** The files queued for the reply being written. */
export function AttachmentPicker({
  files,
  onChange,
  disabled,
}: {
  files: File[];
  onChange: (files: File[]) => void;
  disabled?: boolean;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  const accept = (incoming: FileList | null) => {
    if (!incoming || incoming.length === 0) return;

    const next = [...files];
    let refusal: string | null = null;
    for (const file of Array.from(incoming)) {
      const reason = attachmentProblem(file, next.length);
      if (reason) {
        refusal ??= reason;
        continue;
      }
      next.push(withEffectiveType(file));
    }

    setProblem(refusal);
    onChange(next);
    // Without this, picking the same file twice in a row is a no-op.
    if (inputRef.current) inputRef.current.value = "";
  };

  const full = files.length >= MAX_ATTACHMENTS_PER_MESSAGE;

  return (
    <div className="space-y-2">
      <div
        onDragOver={(event) => {
          if (disabled || full) return;
          event.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(event) => {
          if (disabled || full) return;
          event.preventDefault();
          setDragging(false);
          accept(event.dataTransfer.files);
        }}
        className={cx(
          "flex items-center justify-between gap-3 border border-dashed px-3 py-2 text-sm transition-colors duration-200 ease-smooth",
          dragging
            ? "border-accent bg-accent-soft/50 text-accent-hover"
            : "border-line text-fg-subtle",
          (disabled || full) && "opacity-60",
        )}
      >
        <span className="flex min-w-0 items-center gap-2">
          <Icon name="paperclip" className="size-3.5 shrink-0" />
          {/* `min-w-0` as well as `truncate`: a nowrap string is a flex item's
              automatic minimum size, and without it this row sets a floor that
              scrolls the whole page sideways on a narrow window. */}
          <span className="min-w-0 truncate">
            {full ? `${MAX_ATTACHMENTS_PER_MESSAGE} files is the limit` : "Drop a file, up to 10 MB"}
          </span>
        </span>

        <button
          type="button"
          disabled={disabled || full}
          onClick={() => inputRef.current?.click()}
          className="shrink-0 border border-line bg-raised px-2 py-1 text-xs text-fg-muted transition-colors duration-200 ease-smooth hover:border-line-strong hover:text-fg disabled:opacity-50"
        >
          Choose
        </button>

        <input
          ref={inputRef}
          type="file"
          multiple
          accept={ATTACHMENT_ACCEPT}
          className="hidden"
          onChange={(event) => accept(event.target.files)}
        />
      </div>

      {problem && <p className="text-xs text-danger">{problem}</p>}

      {files.length > 0 && (
        <ul className="space-y-1.5">
          {files.map((file, index) => (
            <li
              key={`${file.name}-${index}`}
              className="flex items-center gap-2 border border-line bg-surface px-2.5 py-1.5 text-xs"
            >
              <Icon name="paperclip" className="size-3.5 shrink-0 text-fg-subtle" />
              <span className="min-w-0 flex-1 truncate text-fg-muted">{file.name}</span>
              <span className="shrink-0 tabular-nums text-fg-subtle">{formatBytes(file.size)}</span>
              <button
                type="button"
                disabled={disabled}
                onClick={() => onChange(files.filter((_, position) => position !== index))}
                aria-label={`Remove ${file.name}`}
                className="shrink-0 text-fg-subtle transition-colors hover:text-danger disabled:opacity-50"
              >
                <Icon name="close" className="size-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
